// Calls our own backend, which proxies to Kiwi/Tequila (they block browser CORS).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
const KIWI_SEARCH_URL = `${BACKEND_URL}/api/kiwi/search`;

// Cache and rate limiting settings
const cache = new Map();
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour
const QUOTA_WINDOW = 60 * 1000; // 1 minute
const QUOTA_LIMIT = 30; // 30 requests per minute
const requestTimes = [];
let requestQueue = Promise.resolve();

// Trip planning constants — tunable.
const SPORT_DURATION_HRS = {
  NBA: 2.5, NFL: 3.5, MLB: 3, NHL: 2.5, MLS: 2, NCAAF: 3.5,
};
const DEFAULT_DURATION_HRS = 3;
const PRE_EVENT_BUFFER_HRS = 4;   // airport → venue + check-in + food
const POST_EVENT_BUFFER_HRS = 2;  // crowd exit + airport security
const EVENING_GAME_HOUR = 17;     // games at/after 5pm use same-day arrival

const checkQuota = async () => {
  const now = Date.now();
  while (requestTimes.length && requestTimes[0] < now - QUOTA_WINDOW) {
    requestTimes.shift();
  }
  if (requestTimes.length >= QUOTA_LIMIT) {
    const waitTime = QUOTA_WINDOW - (now - requestTimes[0]);
    await new Promise(resolve => setTimeout(resolve, waitTime));
    return checkQuota();
  }
  requestTimes.push(now);
};

// DD/MM/YYYY (Kiwi's date format)
const formatKiwiDate = (date) => {
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatHHMM = (date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
const addHours = (date, hours) => new Date(date.getTime() + hours * 3600_000);
const addDays = (date, days) => { const d = new Date(date); d.setDate(d.getDate() + days); return d; };

/**
 * Build the trip strategies to compare for an event.
 * - Afternoon games (< 5pm): one strategy — day-before arrival, same-day late return.
 * - Evening games  (≥ 5pm): two strategies — (a) next-day return (1 hotel night),
 *                                            (b) same-evening red-eye (0 hotel nights).
 * Multiple strategies run in parallel; cheapest wins.
 */
const buildEventStrategies = (eventStart, sport) => {
  const sportHours = SPORT_DURATION_HRS[sport] ?? DEFAULT_DURATION_HRS;
  const eventEnd = addHours(eventStart, sportHours);
  const arriveBy = formatHHMM(addHours(eventStart, -PRE_EVENT_BUFFER_HRS));
  const departAfter = formatHHMM(addHours(eventEnd, POST_EVENT_BUFFER_HRS));

  if (eventStart.getHours() >= EVENING_GAME_HOUR) {
    return [
      {
        strategy: 'next-day-return',
        outboundFrom: eventStart, outboundTo: eventStart,
        returnFrom: addDays(eventStart, 1), returnTo: addDays(eventStart, 1),
        atimeTo: arriveBy,
      },
      {
        strategy: 'same-evening-return',
        outboundFrom: eventStart, outboundTo: eventStart,
        returnFrom: eventStart, returnTo: eventStart,
        atimeTo: arriveBy,
        retDtimeFrom: departAfter,
      },
    ];
  }

  return [{
    strategy: 'day-before-arrival',
    outboundFrom: addDays(eventStart, -1), outboundTo: addDays(eventStart, -1),
    returnFrom: eventStart, returnTo: eventStart,
    retDtimeFrom: departAfter,
  }];
};

const callKiwi = async (params) => {
  const response = await fetch(`${KIWI_SEARCH_URL}?${params}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    if (response.status === 429) {
      const err = new Error('rate-limited');
      err.rateLimited = true;
      throw err;
    }
    throw new Error(`Kiwi proxy error ${response.status}`);
  }
  return response.json();
};

const buildBaseParams = (fromCode, toCode, win) => new URLSearchParams({
  fly_from: fromCode,
  fly_to: toCode,
  date_from: formatKiwiDate(win.outboundFrom),
  date_to: formatKiwiDate(win.outboundTo),
  return_from: formatKiwiDate(win.returnFrom),
  return_to: formatKiwiDate(win.returnTo),
  adults: 1,
  children: 0,
  selected_cabins: 'M',
  adult_hold_bag: '0',
  adult_hand_bag: '1',
  max_fly_duration: 20,
  max_stopovers: 2,
  max_sector_stopovers: 1,
  partner_market: 'us',
  limit: 1,
  curr: 'USD',
});

const formatFlight = (flight, strategy) => ({
  price: flight.price,
  airlines: flight.airlines,
  duration: {
    departure: flight.duration?.departure,
    return: flight.duration?.return,
    total: flight.duration?.total,
  },
  deep_link: flight.deep_link,
  route: flight.route,
  tripStrategy: strategy,
  outboundDeparture: flight.local_departure,
  outboundArrival: flight.local_arrival,
  returnDeparture: flight.route?.find(r => r.return === 1)?.local_departure ?? null,
  flyFrom: flight.flyFrom,
  flyTo: flight.flyTo,
});

/** Run a single strategy: cache check → quota → Kiwi call → format. Returns null on miss/error. */
const runStrategy = async (fromCode, toCode, eventStartIso, strategy) => {
  const cacheKey = `${fromCode}-${toCode}-${eventStartIso}-${strategy.strategy}`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) return cached.data;

  try {
    await checkQuota();
    const params = buildBaseParams(fromCode, toCode, strategy);
    if (strategy.atimeTo) params.append('atime_to', strategy.atimeTo);
    if (strategy.retDtimeFrom) params.append('ret_dtime_from', strategy.retDtimeFrom);

    const data = await callKiwi(params);
    const flight = data.data?.[0];
    const result = flight ? formatFlight(flight, strategy.strategy) : null;
    cache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (err) {
    if (err.rateLimited) return cached?.data || null;
    console.error(`runStrategy(${strategy.strategy}) failed:`, err);
    return null;
  }
};

/**
 * Search Kiwi for the cheapest round-trip that gets the user to a specific event on time.
 * Evening games compare same-evening red-eye vs next-day return in parallel and return the cheaper.
 * @param fromCode IATA of user's home airport
 * @param toCode IATA of venue's airport
 * @param eventStartIso ISO datetime of event kickoff (e.g. event.dates.start.dateTime)
 * @param sport e.g. 'NBA' — used to estimate game length for return-window logic
 */
export const searchEventFlights = async (fromCode, toCode, eventStartIso, sport) => {
  if (!fromCode || !toCode || !eventStartIso) return null;

  const eventStart = new Date(eventStartIso);
  if (isNaN(eventStart) || eventStart.getTime() < Date.now()) return null;

  const strategies = buildEventStrategies(eventStart, sport);

  return new Promise((resolve) => {
    // Take one queue slot per event; inside it, run all strategies in parallel.
    requestQueue = requestQueue.then(async () => {
      const results = await Promise.all(
        strategies.map(s => runStrategy(fromCode, toCode, eventStartIso, s))
      );
      const valid = results.filter(r => r != null && typeof r.price === 'number');
      if (valid.length === 0) { resolve(null); return; }

      const cheapest = valid.reduce((a, b) => (a.price <= b.price ? a : b));
      // Attach the alternative price (if any) so the UI can show "vs $X red-eye" etc.
      const alt = valid.find(r => r !== cheapest);
      cheapest.alternativeStrategy = alt ? { strategy: alt.tripStrategy, price: alt.price } : null;
      resolve(cheapest);
    });
  });
};

/**
 * @deprecated Use searchEventFlights — this kept for backward compatibility.
 * Searches a fixed depart→return date pair with no time-of-day awareness.
 */
export const searchFlights = async (fromCode, toCode, departDate, returnDate) => {
  return new Promise((resolve) => {
    requestQueue = requestQueue.then(async () => {
      const cacheKey = `${fromCode}-${toCode}-${departDate}-${returnDate}`;
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        resolve(cached.data);
        return;
      }
      try {
        await checkQuota();
        const win = {
          outboundFrom: departDate, outboundTo: departDate,
          returnFrom: returnDate, returnTo: returnDate,
        };
        const data = await callKiwi(buildBaseParams(fromCode, toCode, win));
        const flight = data.data?.[0];
        const result = flight ? formatFlight(flight, 'legacy') : null;
        cache.set(cacheKey, { data: result, timestamp: Date.now() });
        resolve(result);
      } catch (err) {
        if (err.rateLimited) { resolve(cached?.data || null); return; }
        console.error('searchFlights failed:', err);
        resolve(null);
      }
    });
  });
};
